import { useLiveQuery } from 'dexie-react-hooks';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { addDays, DISMISS_REASONS, type ReminderRecord, type ReminderType } from '@dairy/shared';
import { useProfile } from '@/auth/useSession';
import { toast } from '@/components/toast';
import { Badge, Button, Card, Dialog, EmptyState, Field, PageHeader, Select, Textarea, buttonVariants } from '@/components/ui';
import { db } from '@/db/db';
import { save } from '@/db/repo';
import { useHerd, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { cn } from '@/lib/cn';

export const UPCOMING_DAYS = 14;

/** BR-9 grouping. A heat watch is "today" during its window and overdue only after it. */
export function reminderBuckets(reminders: ReminderRecord[], today: string) {
  const open = reminders.filter((r) => r.status === 'OPEN').sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const end = (r: ReminderRecord) => (r.type === 'HEAT_WATCH' && r.windowEnd ? r.windowEnd : r.dueDate);
  return {
    overdue: open.filter((r) => end(r) < today),
    today: open.filter((r) => r.dueDate <= today && end(r) >= today),
    upcoming: open.filter((r) => r.dueDate > today && r.dueDate <= addDays(today, UPCOMING_DAYS)),
  };
}

/** The form that resolves a reminder (S3-T09). */
export function actionFor(r: ReminderRecord): string {
  const map: Record<ReminderType, string> = {
    HEAT_WATCH: 'HEAT',
    PREG_CHECK_DUE: 'PREG_CHECK',
    RESERVE_DUE: 'SERVICE',
    BREEDING_START: 'SERVICE',
    HEIFER_BREEDING: 'SERVICE',
    DRY_OFF_DUE: 'DRY_OFF',
    CALVING_DUE: 'CALVING',
    VACCINATION_DUE: 'VACCINATION',
    DEWORMING_DUE: 'DEWORMING',
  };
  const q = new URLSearchParams({ animalId: r.animalId, type: map[r.type] });
  if (r.protocolId) q.set('protocolId', r.protocolId);
  return `/events/new?${q.toString()}`;
}

/** Screen 13 — Reminders (BR-9). */
export function RemindersPage() {
  const { t } = useTranslation();
  const today = useToday();
  const reminders = useLiveQuery(() => db.reminders.where('status').equals('OPEN').toArray(), []) ?? [];
  const b = reminderBuckets(reminders, today);
  return (
    <div className="space-y-5">
      <PageHeader title={t('reminders.title')} />
      <Section title={t('reminders.overdue')} tone="red" list={b.overdue} today={today} />
      <Section title={t('reminders.today')} tone="amber" list={b.today} today={today} />
      <Section title={t('reminders.next14')} tone="neutral" list={b.upcoming} today={today} />
    </div>
  );
}

function Section({ title, tone, list, today }: { title: string; tone: 'red' | 'amber' | 'neutral'; list: ReminderRecord[]; today: string }) {
  return (
    <section>
      <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold">
        {title} <Badge tone={tone}>{list.length}</Badge>
      </h2>
      <ReminderList reminders={list} today={today} />
    </section>
  );
}

export function ReminderList({ reminders, today, closed }: { reminders: ReminderRecord[]; today: string; closed?: boolean }) {
  const { t } = useTranslation();
  const f = useFormat();
  const profile = useProfile();
  const herd = useHerd(today);
  const [dismissing, setDismissing] = useState<ReminderRecord | null>(null);
  if (reminders.length === 0) return <EmptyState>{t('reminders.none')}</EmptyState>;
  const done = async (r: ReminderRecord) => {
    await save('reminder', { ...r, status: 'DONE', completedAt: new Date().toISOString(), completedBy: profile.userId });
    toast(t('common.saved'));
  };
  return (
    <>
      <ul className="space-y-2">
        {reminders.map((r) => {
          const animal = herd.byId.get(r.animalId)?.animal;
          const due =
            r.type === 'HEAT_WATCH' && r.windowEnd ? t('reminders.watchWindow', { from: f.short(r.dueDate), to: f.short(r.windowEnd) }) : f.date(r.dueDate);
          return (
            <li key={r.id}>
              <Card className="p-3">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">
                      <Link to={`/animals/${r.animalId}`}>{animal?.tag ?? '?'}</Link> · {t(`reminders.type.${r.type}`)}
                    </div>
                    <div className={cn('text-sm', !closed && r.dueDate < today ? 'text-red-700' : 'text-stone-600')}>{due}</div>
                    {closed ? (
                      <div className="text-sm text-stone-500">
                        {t(`reminders.status.${r.status}`)}
                        {r.note ? ` · ${t(`reminders.note.${r.note}`, { defaultValue: r.note })}` : ''}
                      </div>
                    ) : null}
                  </div>
                  {!closed ? (
                    <Link to={actionFor(r)} className={buttonVariants({ size: 'sm' })}>
                      {t(`reminders.action.${r.type}`)}
                    </Link>
                  ) : null}
                </div>
                {!closed ? (
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => void done(r)}>
                      <Check className="h-4 w-4" /> {t('reminders.done')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDismissing(r)}>
                      <X className="h-4 w-4" /> {t('reminders.dismiss')}
                    </Button>
                  </div>
                ) : null}
              </Card>
            </li>
          );
        })}
      </ul>
      <DismissDialog reminder={dismissing} onClose={() => setDismissing(null)} />
    </>
  );
}

function DismissDialog({ reminder, onClose }: { reminder: ReminderRecord | null; onClose: () => void }) {
  const { t } = useTranslation();
  const profile = useProfile();
  const [reason, setReason] = useState<string>('NOT_NEEDED');
  const [other, setOther] = useState('');
  const submit = async () => {
    if (!reminder) return;
    const note = reason === 'OTHER' ? other.trim() : reason;
    await save('reminder', { ...reminder, status: 'DISMISSED', completedAt: new Date().toISOString(), completedBy: profile.userId, note });
    toast(t('common.saved'));
    onClose();
  };
  return (
    <Dialog
      open={!!reminder}
      onOpenChange={(o) => !o && onClose()}
      title={t('reminders.dismissTitle')}
      footer={
        <Button disabled={reason === 'OTHER' && !other.trim()} onClick={() => void submit()}>
          {t('reminders.dismiss')}
        </Button>
      }
    >
      <Field label={t('reminders.dismissReason')}>
        <Select value={reason} onChange={(e) => setReason(e.target.value)}>
          {DISMISS_REASONS.map((r) => (
            <option key={r} value={r}>
              {t(`reminders.note.${r}`)}
            </option>
          ))}
        </Select>
      </Field>
      {reason === 'OTHER' ? (
        <Field label={t('common.note')} className="mt-3">
          <Textarea value={other} onChange={(e) => setOther(e.target.value)} maxLength={500} />
        </Field>
      ) : null}
    </Dialog>
  );
}
